// src/components/superuser/PortfolioDashboard.js
//
// Superuser landing page: portfolio health for one client.
// Top = KPI row, bottom = 3D globe (color-coded by health) beside the ranked
// "Properties Needing Attention" table. Clicking a property (globe point or
// table row) drills into it by setting the active property + client, reusing
// the existing manager property views.

import React, { Suspense, useContext, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import PublicIcon from "@mui/icons-material/Public";
import PropertyContext from "../../contexts/PropertyContext";
import usePortfolioData from "../../hooks/usePortfolioData";

const PortfolioGlobe = React.lazy(() => import("./PortfolioGlobe"));

// Compact KPI card (DashboardWidget is a fixed 300px click target — too rigid
// for a dense KPI row, so this is a purpose-built tile).
function KpiCard({ label, value, tone = "default", loading }) {
  const color =
    tone === "bad" ? "error.main" : tone === "warn" ? "warning.main" : "text.primary";
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, borderRadius: 2, height: "100%", minWidth: 0 }}
    >
      <Typography variant="caption" color="text.secondary" noWrap>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ mt: 0.5, color, fontWeight: 600 }}>
        {loading ? "—" : value}
      </Typography>
    </Paper>
  );
}

export default function PortfolioDashboard(props) {
  const ctx = useContext(PropertyContext);
  const clientId = ctx.selectedClient || props.selectedClient || props.clientId;
  const navigate = useNavigate();
  const { rows, ranked, totals, loading } = usePortfolioData(clientId);

  // Returning here from a drilled-in property resets portfolio mode so the
  // dashboard shows the whole client and per-property listeners tear down.
  useEffect(() => {
    if (clientId && ctx.selectedProperty && ctx.selectedProperty !== "all-properties") {
      ctx.setSelectedProperty("all-properties", clientId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.selectedProperty, clientId]);

  const drillInto = (row) => {
    if (!row?.id) return;
    ctx.setSelectedProperty(row.id, row.clientId || clientId);
    navigate("/rooms");
  };

  // Globe points: only properties that have coordinates.
  const points = useMemo(
    () =>
      rows
        .filter((r) => r.hasLocation)
        .map((r) => ({
          lat: r.lat,
          lng: r.lng,
          propertyId: r.id,
          clientId: r.clientId,
          name: r.displayName,
          health: r.health,
          bucketLabel: r.bucket.label,
          color: r.bucket.color,
          altitude: 0.02 + Math.min((r.downRooms || 0) / 200, 0.25),
          radius: 0.4 + Math.min((r.totalRooms || 0) / 500, 0.6),
          downRooms: r.downRooms || 0,
          openWorkOrders: r.openWorkOrders || 0,
        })),
    [rows]
  );

  const missingCoords = rows.filter((r) => !r.hasLocation).length;

  const kpis = [
    { label: "Properties", value: totals.properties },
    { label: "Total Rooms", value: totals.rooms },
    { label: "Down Rooms", value: totals.down, tone: "bad" },
    { label: "Open Work Orders", value: totals.openWorkOrders, tone: "warn" },
    { label: "Open Issues", value: totals.openIssues, tone: "warn" },
    { label: "Needs Vendor", value: totals.needsVendor, tone: "warn" },
    { label: "Occupied", value: totals.occupied },
    { label: "Needs Attention", value: totals.attention, tone: totals.attention ? "bad" : "default" },
  ];

  if (!clientId) {
    return (
      <Typography color="text.secondary" sx={{ mt: 4 }}>
        No client is associated with this account. A superuser needs a{" "}
        <code>clientId</code> on their user document.
      </Typography>
    );
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <PublicIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Portfolio Health
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {clientId}
        </Typography>
      </Box>

      {/* KPI ROW */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {kpis.map((k) => (
          <Grid item xs={6} sm={4} md={3} lg={1.5} key={k.label}>
            <KpiCard {...k} loading={loading} />
          </Grid>
        ))}
      </Grid>

      {/* GLOBE + ATTENTION TABLE */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
            {points.length === 0 ? (
              <Box
                sx={{
                  height: 520,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  color: "text.secondary",
                  gap: 1,
                }}
              >
                <PublicIcon sx={{ fontSize: 48, opacity: 0.4 }} />
                <Typography>No mapped properties yet.</Typography>
                <Typography variant="caption">
                  Add map coordinates in each property's Settings → Map Location
                  to place it on the globe.
                </Typography>
              </Box>
            ) : (
              <Suspense
                fallback={
                  <Box
                    sx={{
                      height: 520,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress />
                  </Box>
                }
              >
                <PortfolioGlobe
                  points={points}
                  onSelect={(d) =>
                    drillInto({ id: d.propertyId, clientId: d.clientId })
                  }
                />
              </Suspense>
            )}
            {missingCoords > 0 && points.length > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", px: 1, py: 0.5 }}
              >
                {missingCoords} propert{missingCoords === 1 ? "y is" : "ies are"}{" "}
                missing map coordinates and not shown on the globe.
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Properties Needing Attention
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Ranked by operational risk (worst first)
              </Typography>
            </Box>
            <TableContainer sx={{ maxHeight: 470 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Property</TableCell>
                    <TableCell align="center">Health</TableCell>
                    <TableCell align="right">Down</TableCell>
                    <TableCell align="right">WOs</TableCell>
                    <TableCell align="right">Issues</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && ranked.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : ranked.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No properties found for this client.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    ranked.map((r) => (
                      <TableRow
                        key={r.id}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => drillInto(r)}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {r.displayName}
                          </Typography>
                          {r.address && (
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {r.address}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={`Health ${r.health} / 100`}>
                            <Chip
                              size="small"
                              label={r.bucket.label}
                              sx={{
                                bgcolor: r.bucket.color,
                                color: "#fff",
                                fontWeight: 600,
                              }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">{r.downRooms || 0}</TableCell>
                        <TableCell align="right">{r.openWorkOrders || 0}</TableCell>
                        <TableCell align="right">{r.openIssues || 0}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
